import { supabase } from '../lib/supabase';

// Fetch a signed URL for a chapter MP3
// Returns null if no audio file found for this combo
export async function getAudioUrl(book, chapter, voice) {
  const { data: fileData, error } = await supabase?.from('bible_audio_files')?.select('id, mp3_path, duration_secs, verses')?.eq('book', book)?.eq('chapter', chapter)?.eq('voice', voice)?.single();

  if (error || !fileData) return null;

  const { data: signedData, error: signErr } = await supabase?.storage?.from('bible-audio')?.createSignedUrl(fileData?.mp3_path, 3600); // 1 hour

  if (signErr || !signedData) return null;

  return {
    url: signedData?.signedUrl,
    audioFileId: fileData?.id,
    durationSecs: fileData?.duration_secs,
  };
}

// Fetch all marks for a chapter ordered by time_seconds ascending.
// Returns { marks: [], hasChunkOffsetBug: false }
// Detects non-monotonic verse-start timestamps which indicate a chunk offset bug
// from multi-chunk audio generation (--no-resume was not used).
export async function getAudioMarks(book, chapter, voice) {
  const { data: fileData } = await supabase?.from('bible_audio_files')?.select('id')?.eq('book', book)?.eq('chapter', chapter)?.eq('voice', voice)?.single();

  if (!fileData) return { marks: [], hasChunkOffsetBug: false };

  const { data: rawMarks, error } = await supabase
    ?.from('bible_audio_marks')
    ?.select('ref, book, chapter, verse, is_chunk_mark, time_seconds')
    ?.eq('audio_file_id', fileData?.id)
    ?.order('time_seconds', { ascending: true });

  if (error) return { marks: [], hasChunkOffsetBug: false };

  // 1. Sort all marks by time_seconds ascending
  const marks = (rawMarks || [])?.sort((a, b) => a?.time_seconds - b?.time_seconds);

  // 2. Check for non-monotonic verse-start marks (is_chunk_mark = false)
  // These are the actual verse timestamps. If any verse-start mark has a
  // time_seconds LESS than the previous verse-start mark, the chapter was
  // generated with a multi-chunk offset bug (chunk offsets were not applied).
  const verseStartMarks = marks?.filter(m => m?.is_chunk_mark === false);
  let hasChunkOffsetBug = false;
  let prevTime = -Infinity;
  for (const mark of verseStartMarks) {
    if (mark?.time_seconds < prevTime) {
      hasChunkOffsetBug = true;
      break;
    }
    prevTime = mark?.time_seconds;
  }

  if (hasChunkOffsetBug) {
    console.warn(
      `Audio marks for ${book} ${chapter} have non-monotonic timestamps — ` +
      `chapter was generated with a multi-chunk offset bug. ` +
      `Re-generate this chapter with --no-resume to fix verse tracking.`
    );
  }

  return { marks, hasChunkOffsetBug };
}

// Fetch available voices for a book/chapter
export async function getAvailableVoices(book, chapter) {
  const { data, error } = await supabase?.from('bible_audio_files')?.select('voice')?.eq('book', book)?.eq('chapter', chapter);

  if (error || !data) return [];
  return [...new Set(data.map(r => r.voice))];
}
